/**
 * Shared error logging configuration for Apollo Client
 * This provides consistent error logging across different Apollo client instances
 */

/**
 * Creates a timestamp formatted in Israeli time (Asia/Jerusalem)
 */
export const createIsraeliTimestamp = (): string => {
  return new Date().toLocaleString("sv-SE", {
    timeZone: "Asia/Jerusalem",
    hour12: false,
  })
}

export interface ErrorContext {
  method: string
  url: string
  operationName: string
  operationType: string
  variables: any
  timestamp: string
}

export const logGraphQLErrors = (
  errors: readonly any[],
  context: ErrorContext,
): void => {
  errors.forEach(({ message, locations, path, extensions }) => {
    console.error("=== GraphQL Error ===")
    console.error(`Method: ${context.method}`)
    console.error(`URL: ${context.url}`)
    console.error(
      `Operation: ${context.operationName} (${context.operationType})`,
    )
    console.error(`Message: ${message}`)
    console.error(`Path: ${JSON.stringify(path)}`)
    console.error(`Location: ${JSON.stringify(locations)}`)
    console.error(`Variables: ${JSON.stringify(context.variables)}`)
    console.error(`Extensions: ${JSON.stringify(extensions)}`)
    console.error(`Timestamp: ${context.timestamp}`)
    console.error("===================")
  })
}

export const logNetworkError = (
  networkError: any,
  context: ErrorContext,
): void => {
  const statusCode =
    "statusCode" in networkError ? networkError.statusCode : "Unknown"

  console.error("=== Network Error ===")
  console.error(`Method: ${context.method}`)
  console.error(`URL: ${context.url}`)
  console.error(
    `Operation: ${context.operationName} (${context.operationType})`,
  )
  console.error(`Variables: ${JSON.stringify(context.variables)}`)
  console.error(`Status: ${statusCode}`)
  console.error(`Message: ${networkError.message}`)

  // Log additional error details from networkError.result if available
  if (networkError.result && networkError.result.errors) {
    console.error(`GraphQL Errors from Server Response:`)
    networkError.result.errors.forEach((error: any, index: number) => {
      console.error(`  Server Error ${index + 1}:`)
      console.error(`    Message: ${error.message || "No message"}`)
      console.error(`    Path: ${JSON.stringify(error.path)}`)
      console.error(`    Locations: ${JSON.stringify(error.locations)}`)
      console.error(`    Extensions: ${JSON.stringify(error.extensions)}`)
    })
  }

  console.error(`Network Error Details:`, networkError)
  console.error(`Timestamp: ${context.timestamp}`)
  console.error("====================")
}

export const createErrorContext = (
  operation: any,
  graphqlUrl: string,
): ErrorContext => {
  try {
    const operationDefinition = operation?.query?.definitions?.[0]
    const operationType =
      operationDefinition && "operation" in operationDefinition
        ? operationDefinition.operation
        : "unknown"

    // Format timestamp in Israel time (Asia/Jerusalem)
    const timestamp = createIsraeliTimestamp()

    return {
      method: "POST",
      url: graphqlUrl || "Unknown URL",
      operationName: operation?.operationName || "Unknown",
      operationType,
      variables: operation?.variables || {},
      timestamp,
    }
  } catch (error) {
    // Fallback context if anything fails - also use Israeli time
    return {
      method: "POST",
      url: graphqlUrl || "Unknown URL",
      operationName: "Unknown (Error creating context)",
      operationType: "unknown",
      variables: {},
      timestamp: createIsraeliTimestamp(),
    }
  }
}
